import { useState } from 'react';
import { ArrowRightLeft, TrendingUp, TrendingDown, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ExchangeRates() {
    const [amount, setAmount] = useState('100');
    const [fromCurrency, setFromCurrency] = useState('USD');
    const [toCurrency, setToCurrency] = useState('ETB');

    const exchangeRate = 56.45;
    const convertedAmount = (parseFloat(amount || '0') * exchangeRate).toFixed(2);

    const bankRates = [
        { name: 'Commercial Bank of Ethiopia', buyRate: 56.45, sellRate: 56.75 },
        { name: 'Awash Bank', buyRate: 56.40, sellRate: 56.70 },
        { name: 'Dashen Bank', buyRate: 56.50, sellRate: 56.80 },
        { name: 'Bank of Abyssinia', buyRate: 56.42, sellRate: 56.72 },
        { name: 'Wegagen Bank', buyRate: 56.48, sellRate: 56.78 },
        { name: 'United Bank', buyRate: 56.43, sellRate: 56.73 }
    ];

    const weeklyTrend = [
        { day: 'Mon', rate: 56.20 },
        { day: 'Tue', rate: 56.35 },
        { day: 'Wed', rate: 56.30 },
        { day: 'Thu', rate: 56.40 },
        { day: 'Fri', rate: 56.45 },
        { day: 'Sat', rate: 56.50 },
        { day: 'Sun', rate: 56.45 }
    ];

    const maxRate = Math.max(...weeklyTrend.map(d => d.rate));
    const minRate = Math.min(...weeklyTrend.map(d => d.rate));

    return (
        <div className="min-h-screen pt-24 pb-20">
            <section className="bg-gradient-to-br from-emerald-800 to-emerald-900 text-white py-20">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-5xl font-bold mb-6">
                            Live Exchange Rates
                        </h1>
                        <p className="text-xl text-emerald-50">
                            Real-time currency exchange rates from partner banks across Ethiopia.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-12 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="max-w-2xl mx-auto bg-white p-8 rounded-xl shadow-xl">
                        <h2 className="text-2xl font-bold text-emerald-900 mb-6 text-center">Currency Converter</h2>

                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
                                    <input
                                        type="number"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                                        placeholder="Enter amount"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">From</label>
                                    <select
                                        value={fromCurrency}
                                        onChange={(e) => setFromCurrency(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                                    >
                                        <option value="USD">USD - US Dollar</option>
                                        <option value="EUR">EUR - Euro</option>
                                        <option value="GBP">GBP - British Pound</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-center">
                                <div className="bg-emerald-100 p-3 rounded-full">
                                    <ArrowRightLeft className="w-6 h-6 text-emerald-800" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">To</label>
                                    <select
                                        value={toCurrency}
                                        onChange={(e) => setToCurrency(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                                    >
                                        <option value="ETB">ETB - Ethiopian Birr</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Converted Amount</label>
                                    <div className="w-full px-4 py-3 bg-emerald-50 border-2 border-emerald-500 rounded-lg text-2xl font-bold text-emerald-900">
                                        {convertedAmount}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg text-center">
                                <p className="text-gray-600">
                                    Exchange Rate: <span className="font-bold text-emerald-900">1 {fromCurrency} = {exchangeRate} {toCurrency}</span>
                                </p>
                            </div>

                            <div className="flex gap-4">
                                <Link to="/contact" className="flex-1 bg-yellow-500 text-emerald-900 px-6 py-3 rounded-lg text-center font-semibold hover:bg-yellow-400 transition">
                                    Start Sending Money
                                </Link>
                                <Link to="/contact" className="flex-1 bg-emerald-800 text-white px-6 py-3 rounded-lg text-center font-semibold hover:bg-emerald-700 transition">
                                    Pay Bills or Top-up Now
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-12 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-emerald-900 mb-8 text-center">Weekly Exchange Rate Trend</h2>
                    <div className="max-w-4xl mx-auto bg-white p-8 rounded-xl shadow-xl">
                        <div className="flex items-end justify-between h-64 gap-4">
                            {weeklyTrend.map((data, index) => {
                                const height = ((data.rate - minRate) / (maxRate - minRate)) * 100;
                                return (
                                    <div key={index} className="flex-1 flex flex-col items-center">
                                        <div className="text-sm font-semibold text-emerald-900 mb-2">
                                            {data.rate}
                                        </div>
                                        <div
                                            className="w-full bg-gradient-to-t from-emerald-800 to-emerald-600 rounded-t-lg transition-all hover:from-emerald-700 hover:to-emerald-500"
                                            style={{ height: `${Math.max(height, 20)}%` }}
                                        ></div>
                                        <div className="text-sm text-gray-600 mt-2">{data.day}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-8 flex items-center justify-center gap-8">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-green-500" />
                                <span className="text-sm text-gray-600">Trend: +0.25 ETB this week</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-12 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-emerald-900 mb-8 text-center">Bank Exchange Rates</h2>
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-emerald-800 text-white">
                                        <tr>
                                            <th className="px-6 py-4 text-left">Bank Name</th>
                                            <th className="px-6 py-4 text-center">Buy Rate</th>
                                            <th className="px-6 py-4 text-center">Sell Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {bankRates.map((bank, index) => (
                                            <tr key={index} className="hover:bg-gray-50 transition">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <Building2 className="w-5 h-5 text-emerald-800" />
                                                        <span className="font-semibold text-gray-900">{bank.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-emerald-900 font-semibold">{bank.buyRate}</span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-emerald-900 font-semibold">{bank.sellRate}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="mt-6 text-center">
                            <p className="text-sm text-gray-600">Rates updated in real-time. Last update: Today at 10:30 AM</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
